// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, { TitleTypographyStyles } from "@awc/App/Components/Common/TitleTypography";
import { CustomAppState } from "@awc/App/Store/Reducers";
import WishListCard, { WishListCardStyles } from "@awc/Overrides/Components/WishListCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import deleteWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/DeleteWishList";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    view = "view",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.view]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
    // XNG-Change
    deleteWishList,
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    // XNG-Changes
    cardListStyles?: CardListStyles;
    titleTypographyStyles?: TitleTypographyStyles;
    cardContainer?: CardContainerStyles;
    noWishListsGridItem?: GridItemProps;
    title?: TypographyPresentationProps;
    linksListWrapper?: InjectableCss;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },
    },
};

interface State {
    wishListToAction?: WishListModel;
    deleteListModalIsOpen?: boolean;
}

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props, State> {
    // XNG-Change
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;
    // XNG-Change init the state
    constructor(props: Props) {
        super(props);
        this.state = {
            deleteListModalIsOpen: false,
        };
    }
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
        // XNG-Change
        if (this.props.zone?.includes("tab")) {
            if (
                this.props.canViewWishLists &&
                this.props.recentWishListsDataView.value &&
                !this.props.tabs[this.props.zone]
            ) {
                this.props.updateZone(this.props.zone, true);
            } else if (!this.props.canViewWishLists || !this.props.recentWishListsDataView.value) {
                this.props.updateZone(this.props.zone, false);
            }
        }
    }

    componentDidUpdate() {
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };
    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    deleteButtonClickHandler = (wishList: WishListModel) => {
        this.setState({
            wishListToAction: wishList,
            deleteListModalIsOpen: true,
        });
    };
    // <Xng-Change>
    deleteCancelHandler = () => {
        this.setState({
            wishListToAction: undefined,
            deleteListModalIsOpen: false,
        });
    };
    deleteSubmitHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
        if (this.state.wishListToAction) {
            this.props.deleteWishList({
                wishListId: this.state.wishListToAction.id,
                onSuccess: () => this.context.addToast({ body: translate("List Deleted"), messageType: "success" }),
                onComplete(resultProps) {
                    if (!resultProps.error) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    // </Xng-Change>

    render() {
        if (!this.props.canViewWishLists) {
            return null;
        }

        const {
            recentWishListsDataView: { value: recentWishLists },
            myListsPageNavLink,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        // XNG-Changes
        if (this.props.fields.view === "links") {
            if (!recentWishLists || recentWishLists.length === 0) {
                return null;
            }
            return (
                <StyledWrapper {...styles.linksListWrapper}>
                    <StyledWrapper>
                        <Typography {...styles.title}>{translate("My Lists")}</Typography>
                        {recentWishLists.map((wishList, index) => (
                            // XNG-Changes
                            <MyListsDetailsPageTypeLink
                                key={index}
                                title={wishList.name}
                                wishListId={wishList.id}
                                testSelector="wishListCardName"
                            />
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            );
        }
        return (
            <>
                <CardList data-test-selector="cardListRecentWishLists" extendedStyles={styles.cardListStyles}>
                    <TitleTypography
                        linkText={translate("View All")}
                        link={myListsPageUrl}
                        extendedStyles={styles.titleTypographyStyles}
                        displayAsButton={true}
                    >
                        {translate("My Lists")}
                    </TitleTypography>
                    {recentWishLists && recentWishLists.length === 0 && (
                        <GridItem {...styles.noWishListsGridItem}>
                            <Typography {...styles.noWishListsText}>
                                {siteMessage("WishLists_NoWishlistsFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {recentWishLists &&
                        recentWishLists.map(wishList => (
                            <CardContainer key={wishList.id} extendedStyles={styles.cardContainer}>
                                <GridItem {...styles.wishListCardGridItem}>
                                    <WishListCard
                                        wishList={wishList}
                                        addWishListToCart={this.props.addWishListToCart}
                                        extendedStyles={styles.wishListCard}
                                        toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                        toggleHide={() => this.toggleHideClickHandler(wishList)}
                                        // XNG-Change
                                        deleteWishList={() => this.deleteButtonClickHandler(wishList)}
                                    />
                                </GridItem>
                            </CardContainer>
                        ))}
                </CardList>

                {/* XNG-Change */}
                <TwoButtonModal
                    modalIsOpen={!!this.state.deleteListModalIsOpen}
                    headlineText={translate("Delete List")}
                    messageText={`${translate("Are you sure you want to delete")} ${
                        this.state.wishListToAction ? this.state.wishListToAction.name : ""
                    }?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="myListsListSubmitDelete"
                ></TwoButtonModal>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.view,
                displayName: "View Type",
                editorTemplate: "DropDownField",
                defaultValue: "cards",
                options: [
                    {
                        displayName: "Cards",
                        value: "cards",
                    },
                    {
                        displayName: "Links",
                        value: "links",
                    },
                ],
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
